import styled from "@emotion/styled";
import {
  Box,
  Chip,
  Container,
  Stack,
  Theme,
  Typography,
  useMediaQuery,
} from "@mui/material";
import Button from "@mui/material/Button";
import Grid2 from "@mui/material/Unstable_Grid2/Grid2";
import { useTheme } from "@mui/material/styles";
import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { capabilitiesDetailPageList } from "../Pages/Capabilities/Content/_Data";
import { scrollToAnchor } from "../Utils/scroll";
import ContactCTASection from "./ContactCTASection";
import PageSectionBG from "./PageSectionBG";

const Footer: React.FC = () => {
  const { pathname } = useLocation();
  const containerRef = useRef(null);
  const theme = useTheme();

  const hideFooterLinks: boolean = true;

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 600);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 600);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return (
    <PageSectionBG
      style={{ borderRadius: "48px" }}
      bgcolor={theme.palette.common.white}
      paddingBottom={0}
      paddingTop={0}
      id={"footer"}
    >
      <Box
        component="div"
        sx={{
          background: theme.palette.common.white,
          borderRadius: {
            xs: "24px",
            sm: "24px",
            md: "48px",
          },
          position: "relative",
          overflow: "hidden",
        }}
        ref={containerRef}
        paddingBottom={5}
        paddingTop={pathname !== "/contact" ? 0 : 10}
      >
        {pathname !== "/contact" && (
          <Stack
            sx={{
              px: { xs: 1, sm: 3.5, md: 0 },
            }}
          >
            <ContactCTASection
              bgcolor="pink"
              canTilt={true}
              paddingBottom={isMobile ? 2 : 4}
              paddingTop={isMobile ? 0 : 4}
            />
          </Stack>
        )}

        <Container maxWidth="xl" style={{ position: "relative", zIndex: 10 }}>
          {hideFooterLinks ? (
            <Stack
              direction={{ xs: "column", sm: "column", md: "row" }}
              justifyContent="space-between"
              alignItems="center"
              gap={1}
            >
              <Box component="div" flexGrow={1}>
                <Button
                  component={Link}
                  to="/"
                  title="Pencil to Polish"
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToAnchor({
                      title: "",
                      link: "/#/hero-section",
                    });
                  }}
                >
                  <img
                    src={"/pencil_to_polish_logo_dark.svg"}
                    alt="Pencil to Polish"
                    width="200"
                    loading="lazy"
                    decoding="async"
                  />
                </Button>
              </Box>

              <Chip
                variant="outlined"
                sx={{
                  borderColor: theme.palette.primary.main,
                  color: theme.palette.primary.main,
                  backgroundColor: "transparent",
                  textTransform: "uppercase",
                  pointerEvents: "none",
                  borderRadius: "4px",
                  scale: {
                    xs: 0.8,
                    sm: 0.85,
                    md: 1,
                  },
                }}
                label={
                  <Stack spacing={1.5} direction="row" alignItems="center">
                    <Typography
                      variant="body1"
                      color="primary"
                      fontWeight="600"
                    >
                      DESIGN
                    </Typography>
                    <Typography
                      variant="body2"
                      color="primary"
                      fontWeight="900"
                      fontSize="0.7rem"
                    >
                      x
                    </Typography>
                    <Typography
                      variant="body1"
                      color="primary"
                      fontWeight="600"
                    >
                      DEVELOPMENT
                    </Typography>
                    <Typography
                      variant="body2"
                      color="primary"
                      fontWeight="900"
                      fontSize="0.7rem"
                    >
                      x
                    </Typography>
                    <Typography
                      variant="body1"
                      color="primary"
                      fontWeight="600"
                    >
                      INNOVATION
                    </Typography>
                  </Stack>
                }
              />
            </Stack>
          ) : (
            <Stack direction={"column"} width={"100%"}>
              <Grid2 container>
                <Grid2 xs={12} sm={4}>
                  <Stack justifyContent="start" alignItems="start" gap={1}>
                    <NavLinks>
                      <Button color="inherit" component={Link} to="/">
                        <Typography
                          variant="textXLMedium"
                          color={theme.palette.common.white}
                        >
                          Home
                        </Typography>
                      </Button>
                      <Button color="inherit" component={Link} to="/about">
                        <Typography
                          variant="textXLMedium"
                          color={theme.palette.common.white}
                        >
                          What we do
                        </Typography>
                      </Button>
                      <Button
                        color="inherit"
                        component={Link}
                        to="/capabilities"
                      >
                        <Typography
                          variant="textXLMedium"
                          color={theme.palette.common.white}
                        >
                          How we do it
                        </Typography>
                      </Button>
                      <Button color="inherit" component={Link} to="/work">
                        <Typography
                          variant="textXLMedium"
                          color={theme.palette.common.white}
                        >
                          Expertise
                        </Typography>
                      </Button>
                      <Button color="inherit" component={Link} to="/studio">
                        <Typography
                          variant="textXLMedium"
                          color={theme.palette.common.white}
                        >
                          Studio
                        </Typography>
                      </Button>
                      <Button
                        color="inherit"
                        component={Link}
                        to="/#/contact"
                        onClick={(e) => {
                          e.preventDefault();
                          scrollToAnchor({
                            title: "Contact",
                            link: "/#/contact",
                          });
                        }}
                      >
                        <Typography
                          variant="textXLMedium"
                          color={theme.palette.common.white}
                        >
                          Contact
                        </Typography>
                      </Button>
                    </NavLinks>
                  </Stack>
                </Grid2>

                <Grid2 xs={12} sm={4}>
                  <Stack justifyContent="start" alignItems="start" gap={1}>
                    <NavLinks>
                      <Typography
                        className="footer-nav-title"
                        variant="overline"
                        color={theme.palette.common.white}
                      >
                        Capabilities
                      </Typography>
                      {capabilitiesDetailPageList.map((project, index) => (
                        <Button
                          key={index}
                          color="inherit"
                          component={Link}
                          to={`/capabilities/${project.slug}`}
                        >
                          <Typography
                            variant="textXLMedium"
                            color={theme.palette.common.white}
                          >
                            {project.title}
                          </Typography>
                        </Button>
                      ))}
                    </NavLinks>
                  </Stack>
                </Grid2>

                <Grid2 xs={12} sm={4}>
                  <Stack justifyContent="start" alignItems="start" gap={1}>
                    <NavLinks>
                      <Typography
                        className="footer-nav-title"
                        variant="overline"
                        color={theme.palette.common.white}
                      >
                        Resources
                      </Typography>
                      <Button color="inherit" component={Link} to="/">
                        <Typography
                          variant="textXLMedium"
                          color={theme.palette.common.white}
                        >
                          FAQs
                        </Typography>
                      </Button>
                      <Button color="inherit" component={Link} to="/">
                        <Typography
                          variant="textXLMedium"
                          color={theme.palette.common.white}
                        >
                          How can AI help Small Business?
                        </Typography>
                      </Button>
                      <Button color="inherit" component={Link} to="/">
                        <Typography
                          variant="textXLMedium"
                          color={theme.palette.common.white}
                        >
                          AI and your business
                        </Typography>
                      </Button>
                      <Button color="inherit" component={Link} to="/">
                        <Typography
                          variant="textXLMedium"
                          color={theme.palette.common.white}
                        >
                          Approach to Design and Engineering
                        </Typography>
                      </Button>
                      <Button color="inherit" component={Link} to="/">
                        <Typography
                          variant="textXLMedium"
                          color={theme.palette.common.white}
                        >
                          AI and Small Business
                        </Typography>
                      </Button>
                      <Button color="inherit" component={Link} to="/">
                        <Typography
                          variant="textXLMedium"
                          color={theme.palette.common.white}
                        >
                          AI for Business Efficiencies
                        </Typography>
                      </Button>
                    </NavLinks>
                  </Stack>
                </Grid2>
              </Grid2>
            </Stack>
          )}
        </Container>

        <CenteredText>
          <FooterParallaxTextWrapper>
            {/* <ParallaxText
                scrollOffset={["start start", "end end"]}
                containerRef={containerRef}
                baseVelocity={-0.35}
                disableTop={true}
              >
                pencil To Polish
              </ParallaxText> */}
          </FooterParallaxTextWrapper>
        </CenteredText>
      </Box>
      <Copyright pt={1} pb={4} theme={theme}>
        <Container maxWidth="xl">
          <Stack
            direction={{
              xs: "column-reverse",
              sm: "column-reverse",
              md: "row",
            }}
            alignItems="center"
            justifyContent="space-between"
            gap={{ xs: 2, sm: 2, md: 0 }}
          >
            <Typography variant="body2">
              Copyright, Pencil to Polish {new Date().getFullYear()}
            </Typography>

            <Stack
              divider={
                useMediaQuery(theme.breakpoints.up("md")) ? <>•</> : null
              }
              direction={{ xs: "column", sm: "column", md: "row" }}
              justifyContent="end"
              alignItems="center"
              flex="1"
              gap={1}
            >
              <Button
                title="Privacy Policy"
                color="primary"
                component={Link}
                to="/privacy-policy"
              >
                <Typography variant="body2">Privacy Policy</Typography>
              </Button>
              <Button
                title="Cookie Policy"
                color="primary"
                component={Link}
                to="/cookie-policy"
              >
                <Typography variant="body2">Cookie Policy</Typography>
              </Button>
              <Button
                title="Terms of Use"
                color="primary"
                component={Link}
                to="/terms"
              >
                <Typography variant="body2">Terms of Use</Typography>
              </Button>
              <Button
                className="termly-display-preferences"
                title="Cookie Settings"
                color="primary"
                component={Link}
                to="#"
              >
                <Typography variant="body2">Cookie Settings</Typography>
              </Button>
            </Stack>
          </Stack>
        </Container>
      </Copyright>
    </PageSectionBG>
  );
};

export default Footer;

const NavLinks = styled(Stack)`
  width: 100%;

  .footer-nav-title {
    font-weight: 700;
    font-size: 1rem;
  }

  a {
    justify-content: start;
    text-transform: none;
    width: fit-content;
    padding-left: 0;
  }
`;

const CenteredText = styled(Box)`
  pointer-events: none; /* Prevent interaction with the text */
  transform: translate(-50%, -50%);
  position: absolute;
  text-align: center;
  max-width: 3000px;
  overflow: hidden;
  height: 100%;
  width: 200%;
  z-index: 0;
  left: 50%;
  top: 50%;
`;

const FooterParallaxTextWrapper = styled.div`
  .parallax {
    mix-blend-mode: overlay;
    white-space: nowrap;
    flex-wrap: nowrap;
    overflow: hidden;
    display: flex;

    .scroller {
      font-family: "woonderregular";
      font-size: 25rem !important;
      white-space: nowrap;
      position: relative;
      padding-top: 7rem;
      flex-wrap: nowrap;
      margin: 1rem 0 0;
      display: flex;
      opacity: 0.25;
      top: -18rem;
    }

    span {
      margin-right: 8rem;
      margin-left: 8rem;
      padding: 0 5rem;
      display: block;
      color: black;

      background-image: repeating-linear-gradient(
        4deg,
        #4cff3c,
        #4cff3c,
        #0adac6,
        #0d99ff,
        #ff00f5,
        #fff61f,
        #4cff3c
      );

      -webkit-animation: gradientAnimation 12s linear infinite;
      animation: gradientAnimation 12s linear infinite;
      -webkit-text-fill-color: transparent;
      -webkit-background-clip: text;
      background-size: 100% 400%;
      background-clip: text;
    }

    @-webkit-keyframes gradientAnimation {
      0% {
        background-position-y: 0%;
      }
      100% {
        background-position-y: 100%;
      }
    }

    @keyframes gradientAnimation {
      0% {
        background-position: 50% 100%;
      }
      100% {
        background-position: 50% -33%;
      }
    }
  }
`;

const Copyright = styled(Box)<{ theme: Theme }>`
  background: ${(props) => props.theme.palette.common.white};
  color: ${(props) => props.theme.palette.primary.main};
  font-family: "Poppins";
  text-align: center;
  font-size: 1rem;

  a {
    text-transform: inherit;
  }
`;
